import { useEffect, useImperativeHandle, useState } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { Stack } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrganisationsAutocomplete } from '~components';
import ResourceGroupAutocomplete from '~components/inputFields/autocompletes/resourceGroupAutocomplete';
import type { AssignDeviceFormResults } from '~features/devices';
import { useFormContainer } from '~hooks';
import ResourceGroup from '~interfaces/resourceGroup';

import schema from './assignDeviceSchema.json';
import Dimens from '../../../../themes/dimens';

interface RemoteActionsDialogProps {
	showEditOrganisation?: boolean;
	currentlyAssignedOrganisationId?: string | undefined;
	onSubmit: (results: AssignDeviceFormResults) => void;
	onCancel: () => void;
}

interface AssignDeviceFormFields {
	organisationId: string;
	resourceGroupId: string;
}

const AssignDeviceForm = ({
	showEditOrganisation = true,
	currentlyAssignedOrganisationId,
	onSubmit,
	onCancel,
}: RemoteActionsDialogProps) => {
	const { t } = useTranslation();
	const { setDisabled, formRef } = useFormContainer();
	const [selectedResourceGroup, setSelectedResourceGroup] = useState<ResourceGroup | undefined>();
	const [resourceGroupDisabled, setResourceGroupDisabled] = useState(true);
	const [selectedOrganisationId, setSelectedOrganisationId] = useState<string | undefined>(
		currentlyAssignedOrganisationId
	);

	const { getValues, control, formState, watch } = useForm<AssignDeviceFormResults>({
		mode: 'onChange',
		resolver: ajvResolver(schema as JSONSchemaType<AssignDeviceFormFields>),
	});

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => onSubmit?.(getValues() as AssignDeviceFormResults),
			onCancel: () => onCancel?.(),
		}),
		[onSubmit, onCancel, getValues]
	);

	useEffect(() => {
		const organisationValue = watch('organisationId');
		const resourceGroupValue = watch('resourceGroupId');

		const organisationFieldValid =
			(showEditOrganisation && organisationValue != undefined) || !showEditOrganisation;
		const resourceGroupValid = resourceGroupValue != undefined || showEditOrganisation;

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setResourceGroupDisabled(!organisationFieldValid);
		setDisabled?.(!(organisationFieldValid && resourceGroupValid));
	}, [formState.isValid, selectedResourceGroup, setDisabled, showEditOrganisation, watch]);

	return (
		<Stack spacing={Dimens.spacingMd}>
			{showEditOrganisation && (
				<Controller
					name='organisationId'
					control={control}
					render={({ field }) => (
						<OrganisationsAutocomplete
							value={selectedOrganisationId}
							label={t('organisation.labels.single', { ns: 'entities' }) + '*'}
							onValueChanged={(newValue) => {
								setSelectedOrganisationId(newValue?.id);
								setSelectedResourceGroup(undefined);
								field.onChange(newValue?.id);
							}}
							required
							sx={{ width: '100%' }}
						/>
					)}
				/>
			)}
			<Controller
				name='resourceGroupId'
				control={control}
				render={({ field }) => (
					<ResourceGroupAutocomplete
						value={selectedResourceGroup}
						label={t('resourceGroup.labels.single', { ns: 'entities' })}
						disabled={resourceGroupDisabled}
						filterOrganisationId={selectedOrganisationId}
						onValueChanged={(newValue) => {
							setSelectedResourceGroup(newValue);
							field.onChange(newValue?.id);
						}}
						sx={{ width: '100%' }}
					/>
				)}
			/>
		</Stack>
	);
};

export default AssignDeviceForm;
